import { EXPENSE_TYPE_IRREGULAR, EXPENSE_TYPE_ONGOING, normalizeExpenseType } from "../../lib/expenseType.js";
import {
  ANALYSIS_CHART_METRIC_ALL,
  ANALYSIS_CHART_METRIC_CATEGORY,
  ANALYSIS_CHART_METRIC_PAYMENT_METHOD,
  ANALYSIS_CHART_METRIC_SERVICE,
  ANALYSIS_CHART_METRIC_SUPPLIER
} from "../../lib/settings/profileSettings.js";
import { budgetUsagePercent, summarizeBudgetRows, summarizeMonthlyExpenseRows } from "../../lib/finance/budgetMath.js";
import {
  budgetUsageMeterRange,
  formatBudgetUsagePercentLabel
} from "../../lib/finance/budgetUsageMeter.js";
import { filterExpenseRowsThroughToday } from "../../lib/finance/projectionMath.js";
import { formatCurrency } from "../../lib/formatters.js";
import { buildMonthlyExpenseColumns } from "../analysis/analysisChartsData.js";
import {
  HOME_TILE_ARG_CATEGORY,
  HOME_TILE_ARG_PAYMENT_METHOD,
  HOME_TILE_ARG_SERVICE,
  HOME_TILE_ARG_SUPPLIER,
  findHomeTileCatalogEntry,
  homeTileCatalog
} from "./homeTileCatalog.js";

function formatCurrencyValue(value) {
  return {
    kind: "currency",
    text: formatCurrency(value)
  };
}

function formatPlainValue(value) {
  return {
    kind: "plain",
    text: String(value ?? "")
  };
}

function parseExpenseDate(dateText) {
  if (!dateText) {
    return null;
  }

  const parsed = new Date(`${dateText}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isMonth(row, dateContext, monthIndex) {
  const parsed = parseExpenseDate(row.date);
  return parsed
    && parsed.getFullYear() === dateContext.scopedYear
    && parsed.getMonth() === monthIndex;
}

function isYear(row, dateContext) {
  const parsed = parseExpenseDate(row.date);
  return parsed && parsed.getFullYear() === dateContext.scopedYear;
}

function isCurrentMonth(row, dateContext) {
  return isMonth(row, dateContext, dateContext.currentMonthIndex);
}

function isLastMonth(row, dateContext) {
  return dateContext.currentMonthIndex > 0
    ? isMonth(row, dateContext, dateContext.currentMonthIndex - 1)
    : false;
}

function sumAmounts(rows = []) {
  return rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
}

function filterByType(rows, type) {
  if (!type) {
    return rows;
  }

  return rows.filter((row) => normalizeExpenseType(row.type) === type);
}

function buildOptionKey(value) {
  return String(value ?? "").trim();
}

function uniqueOptions(rows, key) {
  const seen = new Set();

  return rows
    .map((row) => buildOptionKey(row[key]))
    .filter(Boolean)
    .filter((value) => {
      if (seen.has(value)) {
        return false;
      }

      seen.add(value);
      return true;
    })
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ value, label: value }));
}

function getArgOptions(argType, expenseRows) {
  if (argType === HOME_TILE_ARG_CATEGORY) {
    return uniqueOptions(expenseRows, "category");
  }

  if (argType === HOME_TILE_ARG_SUPPLIER) {
    return uniqueOptions(expenseRows, "supplier");
  }

  if (argType === HOME_TILE_ARG_SERVICE) {
    return uniqueOptions(expenseRows, "service");
  }

  if (argType === HOME_TILE_ARG_PAYMENT_METHOD) {
    return uniqueOptions(expenseRows, "method");
  }

  return [];
}

function rowMatchesArg(row, argType, argValue) {
  if (!argValue) {
    return false;
  }

  if (argType === HOME_TILE_ARG_CATEGORY) {
    return buildOptionKey(row.category) === argValue;
  }

  if (argType === HOME_TILE_ARG_SUPPLIER) {
    return buildOptionKey(row.supplier) === argValue;
  }

  if (argType === HOME_TILE_ARG_SERVICE) {
    return buildOptionKey(row.service) === argValue;
  }

  if (argType === HOME_TILE_ARG_PAYMENT_METHOD) {
    return buildOptionKey(row.method) === argValue;
  }

  return false;
}

function getArgValueLabel(entry, config) {
  const value = config.args?.[entry.arg?.key];
  return value ? String(value) : null;
}

function getTileLabel(entry, config, t) {
  if (entry.blank) {
    return "";
  }

  if (entry.chart && entry.arg) {
    const argLabel = getArgValueLabel(entry, config);
    return argLabel ? `${t("chart")}: ${argLabel}` : t(entry.labelKey);
  }

  const baseLabel = t(entry.labelKey);
  const argLabel = getArgValueLabel(entry, config);

  return argLabel ? `${baseLabel} ${argLabel}` : baseLabel;
}

function createDateTimeFormatters(now, activeLanguage) {
  return {
    date: new Intl.DateTimeFormat(activeLanguage || undefined, {
      month: "long",
      day: "numeric"
    }).format(now),
    time: new Intl.DateTimeFormat(activeLanguage || undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      hourCycle: "h23"
    }).format(now)
  };
}

function createMetricResolvers({
  budgetSummary,
  currentMonthRows,
  dateTime,
  lastMonthRows,
  monthlyExpenseSummary,
  t,
  yearRows
}) {
  const currentArgValue = (entry, config) => config.args?.[entry.arg?.key];
  const currentArgMonthRows = (entry, config) => (
    currentMonthRows.filter((row) => rowMatchesArg(row, entry.arg?.type, currentArgValue(entry, config)))
  );
  const currentArgYearRows = (entry, config) => (
    yearRows.filter((row) => rowMatchesArg(row, entry.arg?.type, currentArgValue(entry, config)))
  );
  const argMetricValue = (entry, config, rows) => (
    currentArgValue(entry, config)
      ? formatCurrencyValue(sumAmounts(rows))
      : formatPlainValue(t("choose"))
  );

  return {
    "time-now": () => formatPlainValue(dateTime.time),
    "date-today": () => formatPlainValue(dateTime.date),
    "spent-total-this-month": () => formatCurrencyValue(monthlyExpenseSummary.total.spentThisMonth),
    "spent-total-last-month": () => formatCurrencyValue(sumAmounts(lastMonthRows)),
    "spent-total-this-year": () => formatCurrencyValue(budgetSummary.total.spent),
    "spent-ongoing-this-month": () => formatCurrencyValue(monthlyExpenseSummary.ongoing.spentThisMonth),
    "spent-ongoing-last-month": () => formatCurrencyValue(sumAmounts(filterByType(lastMonthRows, EXPENSE_TYPE_ONGOING))),
    "spent-ongoing-this-year": () => formatCurrencyValue(budgetSummary.ongoing.spent),
    "spent-irregular-this-month": () => formatCurrencyValue(monthlyExpenseSummary.irregular.spentThisMonth),
    "spent-irregular-last-month": () => formatCurrencyValue(sumAmounts(filterByType(lastMonthRows, EXPENSE_TYPE_IRREGULAR))),
    "spent-irregular-this-year": () => formatCurrencyValue(budgetSummary.irregular.spent),
    "budget-remaining-this-month": () => formatCurrencyValue(budgetSummary.ongoing.budget - monthlyExpenseSummary.ongoing.spentThisMonth),
    "budget-remaining-this-year": () => formatCurrencyValue(budgetSummary.total.remaining),
    "budget-total-this-year": () => formatCurrencyValue(budgetSummary.total.budget),
    "projection-projected-spend-this-year": () => formatCurrencyValue(budgetSummary.total.projected),
    "projection-implied-spend-this-month": () => formatCurrencyValue(budgetSummary.ongoing.implied),
    "category-spent-this-month": (entry, config) => argMetricValue(entry, config, currentArgMonthRows(entry, config)),
    "category-spent-this-year": (entry, config) => argMetricValue(entry, config, currentArgYearRows(entry, config)),
    "supplier-spent-this-month": (entry, config) => argMetricValue(entry, config, currentArgMonthRows(entry, config)),
    "supplier-spent-this-year": (entry, config) => argMetricValue(entry, config, currentArgYearRows(entry, config)),
    "service-spent-this-month": (entry, config) => argMetricValue(entry, config, currentArgMonthRows(entry, config)),
    "service-spent-this-year": (entry, config) => argMetricValue(entry, config, currentArgYearRows(entry, config)),
    "payment-method-spent-this-month": (entry, config) => argMetricValue(entry, config, currentArgMonthRows(entry, config)),
    "payment-method-spent-this-year": (entry, config) => argMetricValue(entry, config, currentArgYearRows(entry, config))
  };
}

function getChartArgValue(entry, config) {
  const value = config.args?.[entry.arg?.key];
  return value ? String(value) : "";
}

function createMonthlyExpensesChart({ rows, expenseRows, metric, selectedCategory, t }) {
  return {
    type: "monthly-expenses",
    columns: buildMonthlyExpenseColumns({
      budgetRows: rows,
      expenseRows,
      metric,
      selectedCategory,
      t
    }).map((column, index) => ({
      ...column,
      inlineLabel: String(index + 1)
    }))
  };
}

function createTileChart(entry, config, { rows, expenseRows, t }) {
  if (entry.chart === "expenses") {
    return createMonthlyExpensesChart({
      rows,
      expenseRows,
      metric: ANALYSIS_CHART_METRIC_ALL,
      t
    });
  }

  if (entry.chart === "category-expenses") {
    const selectedCategory = getChartArgValue(entry, config);
    if (!selectedCategory) {
      return null;
    }

    return createMonthlyExpensesChart({
      rows,
      expenseRows: expenseRows.filter((row) => buildOptionKey(row.category) === selectedCategory),
      metric: ANALYSIS_CHART_METRIC_CATEGORY,
      selectedCategory,
      t
    });
  }

  if (entry.chart === "supplier-expenses") {
    const selectedSupplier = getChartArgValue(entry, config);
    if (!selectedSupplier) {
      return null;
    }

    return createMonthlyExpensesChart({
      rows,
      expenseRows: expenseRows.filter((row) => buildOptionKey(row.supplier) === selectedSupplier),
      metric: ANALYSIS_CHART_METRIC_SUPPLIER,
      t
    });
  }

  if (entry.chart === "service-expenses") {
    const selectedService = getChartArgValue(entry, config);
    if (!selectedService) {
      return null;
    }

    return createMonthlyExpensesChart({
      rows,
      expenseRows: expenseRows.filter((row) => buildOptionKey(row.service) === selectedService),
      metric: ANALYSIS_CHART_METRIC_SERVICE,
      t
    });
  }

  if (entry.chart === "payment-method-expenses") {
    const selectedPaymentMethod = getChartArgValue(entry, config);
    if (!selectedPaymentMethod) {
      return null;
    }

    return createMonthlyExpensesChart({
      rows,
      expenseRows: expenseRows.filter((row) => buildOptionKey(row.method) === selectedPaymentMethod),
      metric: ANALYSIS_CHART_METRIC_PAYMENT_METHOD,
      t
    });
  }

  return null;
}

function createTileMeter(entry, budgetSummary) {
  if (entry.meter === "ongoing") {
    const value = budgetUsagePercent(budgetSummary.ongoing.implied, budgetSummary.ongoing.budget);

    return {
      value,
      valueLabel: formatBudgetUsagePercentLabel(value),
      range: budgetUsageMeterRange
    };
  }

  if (entry.meter === "total") {
    const value = budgetUsagePercent(budgetSummary.total.projected, budgetSummary.total.budget);

    return {
      value,
      valueLabel: formatBudgetUsagePercentLabel(value),
      range: budgetUsageMeterRange
    };
  }

  return null;
}

export function createHomeEditorOptions(t) {
  return homeTileCatalog.map((entry) => ({
    ...entry,
    label: t(entry.labelKey),
    argument: entry.arg ? t(entry.arg.labelKey) : null
  }));
}

export function createHomeArgOptions(entry, expenseRows) {
  return getArgOptions(entry.arg?.type, expenseRows);
}

export function createHomeTiles(configs, { activeLanguage, rows, expenseRows, dateContext, now, t }) {
  const budgetSummary = summarizeBudgetRows(rows, expenseRows, dateContext);
  const monthlyExpenseSummary = summarizeMonthlyExpenseRows(expenseRows, dateContext);
  const dateTime = createDateTimeFormatters(now, activeLanguage);
  const actualExpenseRows = filterExpenseRowsThroughToday(expenseRows, dateContext);
  const currentMonthRows = actualExpenseRows.filter((row) => isCurrentMonth(row, dateContext));
  const lastMonthRows = expenseRows.filter((row) => isLastMonth(row, dateContext));
  const yearRows = actualExpenseRows.filter((row) => isYear(row, dateContext));
  const metricResolvers = createMetricResolvers({
    budgetSummary,
    currentMonthRows,
    dateTime,
    lastMonthRows,
    monthlyExpenseSummary,
    t,
    yearRows
  });

  return configs.map((config, index) => {
    const entry = findHomeTileCatalogEntry(config.metricKey);
    const chart = createTileChart(entry, config, { rows, expenseRows, t });
    const meter = createTileMeter(entry, budgetSummary);
    const value = entry.chart && entry.arg && !getChartArgValue(entry, config)
      ? formatPlainValue(t("choose"))
      : metricResolvers[entry.key]?.(entry, config) ?? formatPlainValue("");

    return {
      key: `slot-${index}`,
      isBlank: Boolean(entry.blank),
      label: getTileLabel(entry, config, t),
      chart,
      meter,
      value,
      config
    };
  });
}
